/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ExpenseSelectionViewModel } from 'src/app/receipt-long-op/view-models/expense-selection.view-model';
import { ExpenseSelection } from 'src/app/receipt-long-op/domain-models/expense-selection';
import { ExpenseClassification } from 'src/app/receipt-long-op/generated/domain-models/enums/generated-expense-classification';
import { auditTime, distinctUntilChanged, firstValueFrom, fromEvent, map, Observable, skip, take } from 'rxjs';
import { DecodeTextBoxComponent, ExpandableComponent, ExternalListComponent, Filter, LabelledTextBoxComponent, MessageResourceManager, NTSTranslatePipe, SpacerBoxComponent, TextButtonComponent } from '@nts/std';
import { ExpenseModel } from 'src/app/expense-model/domain-models/expense-model';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ExpenseClassificationBoxComponent } from './expense-classification-box/expense-classification-box.component';
import { BaseStepComponent } from '../base-step.component';
import { CurrencyExtViewModel } from 'src/app/receipt-long-op/view-models/currency.ext-view-model';
import { WeaSettingsDataDto } from 'src/app/create-expense-annotation-long-op/api-clients/dto/wea-settings-data.dto';
import getSymbolFromCurrency from 'currency-symbol-map';
import { SDTExternalListComponent } from 'src/app/shared/components/sdt-external-list/sdt-external-list.component';
import { UserOfTenantIdentity } from 'src/app/external-remote/user-of-tenant/user-of-tenant.identity';
import { SupplierNotSavedViewModel } from 'src/app/receipt-long-op/view-models/modals/supplier-not-saved.view-model';
import { ExpensePrefill } from 'src/app/receipt-long-op/domain-models/dto/expense-prefill.dto';
import { ExtendedAvailableExpense } from 'src/app/receipt-long-op/domain-models/extended-available-expense';
import { ToastMessageType } from '@nts/std/types';
import { ColorUtility } from 'src/app/shared/services/color-utility';
import { PropertyViewModelInterface } from '@nts/std/interfaces';
import { FileUploadStatusInterface, MultipleFileUploadComponent } from 'src/app/shared/components/multiple-file-upload/multiple-file-upload.component';
import { FullscreenPreviewComponent } from 'src/app/shared/components/fullscreen-preview/fullscreen-preview.component';
import { ExpenseFileCollectionItemViewModel } from 'src/app/receipt-long-op/view-models/expense-file.collection-item-view-model';
import { ReceiptLongOpOrchestratorViewModel } from 'src/app/receipt-long-op/view-models/receipt-long-op.orchestrator-view-model';
import { ExpenseFileUtils } from 'src/app/receipt-long-op/utils/expense-file.utils';

@UntilDestroy()
@Component({
  selector: 'nts-expense-selection-step',
  templateUrl: './expense-selection-step.component.html',
  styleUrls: ['./expense-selection-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NTSTranslatePipe,
    NgIf,
    SvgIconComponent,
    ExpenseClassificationBoxComponent,
    NgFor,
    AsyncPipe,
    ExpandableComponent,
    LabelledTextBoxComponent,
    ExternalListComponent,
    DecodeTextBoxComponent,
    SpacerBoxComponent,
    SDTExternalListComponent,
    TextButtonComponent,
    MultipleFileUploadComponent,
    FullscreenPreviewComponent
  ]
})
export class ExpenseSelectionStepComponent extends BaseStepComponent<ExpenseSelectionViewModel> {


  orchestratorViewModel: ReceiptLongOpOrchestratorViewModel;

  expenseClassificationList = [];
  expenseTypeEnum = ExpenseSelection;

  expenseModelId: number;
  expenseAnnotationId: number;
  remoteExpense: boolean;

  private configuration: WeaSettingsDataDto = null;
  private currencySymbol: string = null;

  classificationPrefillColor: string | null = null;

  // I componenti data di std si sottoscrivono al view model in modo asincrono: il template va creato dopo il prefill
  isStepReady = false;

  private readonly XS = 576;
  isMobileView = signal<boolean>(false);

  get selectedModelCode(): number {
    return this.rootViewModel.params.expenseModelData.expenseModelRef.modelCode.value;
  }

  get currentExpenseUserIdentity(): UserOfTenantIdentity {
    return this.rootViewModel.params.userRef?.getDomainModel()?.currentIdentity;
  }

  get fullExpenseModel(): ExpenseModel {
    return this.rootViewModel.params.expenseModelData.availableModels.find((e: ExpenseModel) => e.modelCode === this.selectedModelCode);
  }

  untilDestroyed(): <U>(source: Observable<U>) => Observable<U> {
    return untilDestroyed(this);
  }

  async onViewModelLoaded() {
    this.orchestratorViewModel = this.receiptStepsService.orchestratorViewModel;

    this.receiptStepsService.stepLoading.next(true);
    const isReadOnly = await this.isReadOnly(this.rootViewModel);
    this.receiptStepsService.isReadOnly$.next(isReadOnly);

    const queryParams = await firstValueFrom(this.routeStateService.queryParams);
    if (queryParams['expenseModelId']) {
      this.expenseModelId = parseInt(queryParams['expenseModelId'], 10);
    }
    if (queryParams['expenseAnnotationId']) {
      this.expenseAnnotationId = parseInt(queryParams['expenseAnnotationId'], 10);
    }
    this.remoteExpense = queryParams['remoteExpense'] === 'true';

    const configurationResponse = await firstValueFrom(this.createExpenseAnnotationLongOpApiClient.getWEASettingsData());
    if (configurationResponse?.operationSuccedeed === true) {
      this.configuration = configurationResponse.result;
    }

    const isoCode = queryParams['isoCode'];
    if (isoCode?.length > 0) {
      this.currencySymbol = getSymbolFromCurrency(isoCode);
      await this.setCurrencyFromCodeAndDisable(this.rootViewModel.params.expenseData.currencyRef, isoCode);
    } else {
      const currencyName = queryParams['currencyName'];
      if (currencyName?.length > 0) {
        this.currencySymbol = currencyName;
        await this.setCurrencyFromNameAndDisable(this.rootViewModel.params.expenseData.currencyRef, currencyName);
      } else {
        this.rootViewModel.params.expenseData.currencyRef.isEnabled = true;
      }

      this.isMobileView.set(window.innerWidth < this.XS);

      fromEvent(window, 'resize')
        .pipe(auditTime(100), map(() => window.innerWidth), distinctUntilChanged())
        .subscribe(() => this.onResize());
    }

    await this.initExpenseSelection();
    this.applyUploadPrefillClassification();

    await this.initExpenseData();

    await this.initPaymentData();

    await this.applyUploadPrefillFields();

    this.isStepReady = true;
    this.receiptStepsService.stepLoading.next(false);
    this.cd.detectChanges();
  }

  private applyUploadPrefillClassification() {
    const prefill: ExpensePrefill = this.receiptStepsService.uploadDocumentPrefill;
    const prefilledClassification = prefill?.expenseClassification;

    if (prefilledClassification == null) {
      return;
    }

    if (this.expenseClassificationList.includes(prefilledClassification)) {
      this.viewModel.expenseClassification.value = prefilledClassification;
      this.classificationPrefillColor = "#ddd900";
    }
  }

  private async applyUploadPrefillFields() {
    const prefill: ExpensePrefill = this.receiptStepsService.uploadDocumentPrefill;

    if (prefill == null) {
      return;
    }

    this.rootViewModel.externalRetriever.toastMessageService.showToast({
      message: MessageResourceManager.Current.getMessage('UploadPrefillMessage'),
      title: MessageResourceManager.Current.getMessage('std_Information'),
      type: ToastMessageType.info
    });

    if (prefill.expenseClassificationDescription) {
      this.rootViewModel.params.expenseData.description.value = prefill.expenseClassificationDescription;
      this.setPrefillColors(this.rootViewModel.params.expenseData.description);
    }

    if (prefill.expenseDate) {
      this.rootViewModel.params.expenseData.expenseDate.value = prefill.expenseDate;
      this.setPrefillColors(this.rootViewModel.params.expenseData.expenseDate);
    }

    if (prefill.expenseTotalAmount) {
      this.rootViewModel.params.expenseData.expenseTotalAmount.value = null;
      this.rootViewModel.params.expenseData.expenseTotalAmount.value = prefill.expenseTotalAmount;
      this.setPrefillColors(this.rootViewModel.params.expenseData.expenseTotalAmount);
    }

    if (prefill.supplierId) {
      this.rootViewModel.params.paymentData.supplierId.value = prefill.supplierId;
      this.rootViewModel.params.paymentData.supplierRef.id.value = prefill.supplierId;
      this.setPrefillColors(this.rootViewModel.params.paymentData.supplierRef.id);
    }
    else {
      if (prefill.businessName || prefill.vatNumber) {
        this.rootViewModel.params.paymentData.supplierId.value = null;
        await this.rootViewModel.params.paymentData.supplierRef.reset();
      }

      if (prefill.businessName) {
        this.rootViewModel.params.paymentData.businessName.value = prefill.businessName;
        this.setPrefillColors(this.rootViewModel.params.paymentData.businessName);
      }

      if (prefill.vatNumber) {
        this.rootViewModel.params.paymentData.vatNumber.value = prefill.vatNumber;
        this.setPrefillColors(this.rootViewModel.params.paymentData.vatNumber);
      }
    }
  }

  private readonly NEUTRAL_DEFAULT_COLOR = "#BFBFBF";
  private readonly NEUTRAL_ACTIVE_COLOR = "#006FDD";
  private readonly NEUTRAL_HOVER_COLOR = "#004891";

  private setPrefillColors(propertyViewModel: PropertyViewModelInterface) {
    propertyViewModel.defaultColor = "#ddd900";
    propertyViewModel.activeColor = "#ddd900";
    propertyViewModel.hoverColor = ColorUtility.shade("#ddd900", -0.3);

    // Appena il valore viene modificato dall'utente, rimuovo l'evidenziazione del prefill
    propertyViewModel.value$.pipe(
      skip(1),
      take(1),
      this.untilDestroyed()
    ).subscribe(() => {
      propertyViewModel.defaultColor = this.NEUTRAL_DEFAULT_COLOR;
      propertyViewModel.activeColor = this.NEUTRAL_ACTIVE_COLOR;
      propertyViewModel.hoverColor = this.NEUTRAL_HOVER_COLOR;
      this.cd.detectChanges();
    });
  }

  async initExpenseSelection() {
    this.receiptStepsService.nextStepEnabled.next(true);

    // se non ho popolato gli availableModels li popolo
    if (this.rootViewModel.params.expenseModelData.availableModels == null) {

      this.rootViewModel.params.expenseModelData.availableModels = await this.receiptStepsService.orchestratorViewModel.getAvailableModels(
        this.currentExpenseUserIdentity,
        this.rootViewModel.params?.expenseState.value
      );
    }

    if (this.rootViewModel.params.expenseModelData.availableModels.length > 0) {
      const availableExpenses = await this.receiptStepsService.orchestratorViewModel.getAvailableExpenses(
        this.fullExpenseModel,
        this.currentExpenseUserIdentity,
        this.rootViewModel.params?.expenseState.value
      );

      const personalExpenses = await this.receiptStepsService.orchestratorViewModel.getPersonalExpenses(
        this.currentExpenseUserIdentity,
        this.rootViewModel.params.expenseState.value
      );

      if ((availableExpenses && availableExpenses.length > 0) || (personalExpenses && personalExpenses.length > 0)) {
        this.expenseClassificationList = Object.values(ExpenseClassification)
          .filter((v) => !isNaN(Number(v)))
          // devo filtrare l'expenseClassification in base al modello scelto
          // Il modello scelto ha associato una lista di expense type che a sua volta hanno associato un expenseClassification
          .filter((element: number) =>
            availableExpenses?.find((ae) => ae.expenseTypeRef?.refoundClass === element) != null ||

            // Verifico se ho delle spese personali che fanno parte dell'expense classification corrente
            personalExpenses.find((pe) => pe.expenseTypeRef?.refoundClass === element) != null
          );
      }
    }

    this.viewModel = this.rootViewModel.params.expenseSelection;

    this.rootViewModel.params.expenseSelection.initPresentationLogic(
      this.expenseAnnotationId,
      this.remoteExpense,
      this.expenseClassificationList,
      this.expenseModelId,
      this.rootViewModel.params.expenseState,
      !this.receiptStepsService.isReadOnly$.value
    );

  }

  async initExpenseData() {
    const currentExpenseClassification = this.rootViewModel.params.expenseSelection.expenseClassification.value;
    const extendedAvailableExpenses = await this.receiptStepsService.orchestratorViewModel.getExtendedAvailableExpenses(
      this.selectedModelCode,
      currentExpenseClassification,
      this.currentExpenseUserIdentity,
      this.rootViewModel?.params?.expenseState?.value
    );

    const customerIdentity = this.rootViewModel.params.getDomainModel().customerIdentity;
    const leadIdentity = this.rootViewModel.params.getDomainModel().leadIdentity;
    const commissionIdentity = this.rootViewModel.params.getDomainModel().commissionIdentity;

    //disabilito il ref, per far si che si aggiorni in base alla classificazione selezionata
    this.rootViewModel.params.expenseData.extendedExpenseTypeRef.isEnabled = false;

    await this.rootViewModel.params.expenseData.initPresentationLogic(
      currentExpenseClassification,
      this.fullExpenseModel,
      extendedAvailableExpenses,
      this.expenseAnnotationId,
      this.remoteExpense,
      this.currencySymbol,
      this.rootViewModel.params.expenseState,
      this.configuration,
      customerIdentity,
      leadIdentity,
      commissionIdentity,
      this.onlineService,
      this.modalService,
      !this.receiptStepsService.isReadOnly$.value
    );

    this.rootViewModel.params.expenseData.extendedExpenseTypeRef.reloadExternalList();
    this.rootViewModel.params.expenseData.extendedExpenseTypeRef.isEnabled = true;

    await this.autoSelectFirstExpenseType(extendedAvailableExpenses);
  }

  private async autoSelectFirstExpenseType(extendedAvailableExpenses: ExtendedAvailableExpense[]): Promise<void> {
    if (extendedAvailableExpenses?.length > 0 && !(this.rootViewModel.params.expenseData.extendedExpenseTypeRef?.id?.value > 0)) {
      await this.rootViewModel.params.expenseData.extendedExpenseTypeRef.setCodeValue(extendedAvailableExpenses[0].id);
    }
  }

  async initPaymentData(selectedExpenseClassification?: ExpenseClassification) {

    // se non ho popolato gli availableModels li popolo
    if (this.rootViewModel.params.expenseModelData.availableModels == null) {
      this.rootViewModel.params.expenseModelData.availableModels = await this.receiptStepsService.orchestratorViewModel.getAvailableModels(
        this.currentExpenseUserIdentity,
        this.rootViewModel.params?.expenseState.value
      );
    }



    const extendedAvailablePayments = await this.receiptStepsService.orchestratorViewModel.getExtendedAvailablePayments(
      this.selectedModelCode,
      this.currentExpenseUserIdentity,
      this.rootViewModel.params?.expenseState.value
    );


    const expenseClassification = selectedExpenseClassification ?? this.rootViewModel.params.expenseSelection.expenseClassification.value;

    await this.rootViewModel.params.paymentData.initPresentationLogic(
      expenseClassification,
      this.fullExpenseModel,
      extendedAvailablePayments,
      this.expenseAnnotationId,
      this.remoteExpense,
      this.rootViewModel.params.expenseState,
      this.onlineService,
      !(await this.isReadOnly(this.rootViewModel))
    );

    this.cd.detectChanges();
  }

  async selectExpenseClassification(expenseClassification: ExpenseClassification) {
    this.viewModel.expenseClassification.setValue(expenseClassification);
    this.rootViewModel.params.paymentData.currentExpenseClassificationType = expenseClassification;

    this.classificationPrefillColor = null;
    this.telemetryService.trackEvent({
      name: 'WEA_ReceiptLongOp_ExpenseSelectionStep_SelectExpenseClassification',
      properties: {
        expenseClassification: ExpenseClassification[expenseClassification]
      }
    });

    await this.initExpenseData();
    await this.initPaymentData(expenseClassification);
    //Aggiorno l'etichetta dei pulsanti in base alla classificazione selezionata
    this.receiptStepsService.currentStepChanged.next();
    this.cd.detectChanges();
  }

  async setCurrencyFromCodeAndDisable(external: CurrencyExtViewModel, value: string) {
    const isoCodeFilter = new Filter();
    isoCodeFilter.name = "IsoCode";
    isoCodeFilter.value = value;
    const tempDecode = external.decodeProperties;
    external.decodeProperties = [];
    const res = await firstValueFrom(external.execAutocomplete(null, null, false, null, null, null, null, [isoCodeFilter]));
    if (res[0]?.identity && res[0]?.identity['Id'] != null) {
      await external.setCodeValue(res[0]?.identity['Id']);
      external.isEnabled = false;
    } else {
      external.isEnabled = true;
    }
    external.decodeProperties = tempDecode;
  }

  async setCurrencyFromNameAndDisable(external: CurrencyExtViewModel, value: string) {
    const nameFilter = new Filter();
    nameFilter.name = "Name";
    nameFilter.value = value;
    const tempDecode = external.decodeProperties;
    external.decodeProperties = [];
    const res = await firstValueFrom(external.execAutocomplete(null, null, false, null, null, null, null, [nameFilter]));
    if (res[0]?.identity && res[0]?.identity['Id'] != null) {
      await external.setCodeValue(res[0]?.identity['Id']);
      external.isEnabled = false;
    } else {
      external.isEnabled = true;
    }
    external.decodeProperties = tempDecode;
  }

  async addSupplierNotSaved() {

    const oldValueBusinessName = this.rootViewModel.params.paymentData.businessName.value;
    const oldValueVatNumber = this.rootViewModel.params.paymentData.vatNumber.value;

    const supplierNotSavedViewModel = new SupplierNotSavedViewModel(
      this.rootViewModel.params.paymentData,
      this.rootViewModel.externalRetriever.toastMessageService
    );


    const result = await this.modalService.showCustomModalWithResultAsync<boolean>(
      supplierNotSavedViewModel,
      true,
      false,
      false,
      {
        disableClose: true,
      }
    );

    if (result.cancel || result?.result === false) {
      this.rootViewModel.params.paymentData.businessName.value = oldValueBusinessName != "" ? oldValueBusinessName : null;
      this.rootViewModel.params.paymentData.vatNumber.value = oldValueVatNumber != "" ? oldValueVatNumber : null;
    } else {
      this.rootViewModel.params.paymentData.businessName.value = supplierNotSavedViewModel.paymentDataViewModel.businessName.value != "" ? supplierNotSavedViewModel.paymentDataViewModel.businessName.value : null;
      this.rootViewModel.params.paymentData.vatNumber.value = supplierNotSavedViewModel.paymentDataViewModel.vatNumber.value != "" ? supplierNotSavedViewModel.paymentDataViewModel.vatNumber.value : null;
    }

  }

  hasSupplierRef(): boolean {
    const supplierId = this.rootViewModel.params.paymentData?.supplierId?.value;
    const supplierRefId = this.rootViewModel.params.paymentData?.supplierRef?.id?.value;
    return (supplierId != null && supplierId != 0) || (supplierRefId != null && supplierRefId != 0);
  }

  hasManualSupplierData(): boolean {
    if (this.hasSupplierRef()) {
      return false;
    }
    const businessName = this.rootViewModel.params.paymentData.businessName.value;
    const vatNumber = this.rootViewModel.params.paymentData.vatNumber.value;
    return (businessName != null && businessName !== '') || (vatNumber != null && vatNumber !== '');
  }

  showSupplierRefBox(): boolean {
    return (this.onlineService.isOnline || this.hasSupplierRef()) && !this.hasManualSupplierData();
  }

  showManualSupplierBox(): boolean {
    return (!this.onlineService.isOnline && !this.hasSupplierRef()) || this.hasManualSupplierData();
  }

  canAddManualSupplier(): boolean {
    return !this.hasSupplierRef() && !this.hasManualSupplierData();
  }

  deleteManualSupplier() {
    this.rootViewModel.params.paymentData.businessName.value = null;
    this.rootViewModel.params.paymentData.vatNumber.value = null;
  }

  async deleteSupplierRef() {
    await this.rootViewModel.params.paymentData.supplierRef.reset();
    this.deleteManualSupplier();
  }

  onResize() {
    const isMobile = window.innerWidth < this.XS;
    this.isMobileView.set(isMobile);
  }

  viewExpenseFile(item: ExpenseFileCollectionItemViewModel): void {
    ExpenseFileUtils.viewExpenseFile(
      item,
      this.viewModel,
      this.receiptStepsService.orchestratorViewModel,
      this.sanitizer,
      () => this.cd.detectChanges()
    );
  }


  isAdditionalFile(item: FileUploadStatusInterface): boolean {
    return (item as ExpenseFileCollectionItemViewModel).isPrimaryFile.value !== true;
  }
  async fileUploadValidation() {
    await this.viewModel.validateStep();
    this.cd.detectChanges();
  }
}
